import { decamelize } from '@ember/string';

/*

Serialize a JSON object for multipart form submissions.
A nested-JSON object is transformed into a flat JSON object.

### Parameters
@param {String} input - the input that is being converted
@param {String[]} parts - the property that is storing the input. Expressed as
                          a list of strings.
                          Example: ['entity', 'merchant_profile', 'profile_image']
@param {String[]} results - the FormData that is being built
@param {Boolean} needsDecamelized - FormData keys are snake cased by default,
                                    use this flag to convert to camel case.
@param {String[]} nullableParts - Parts that allow null to be passed through. Expressed
                                  as a concatenation of parts separated by `.`
                                  Example: ['entity.merchant_profile.profile_image']

### Returns
@returns {String[]} results

### Examples
Example Input:
  {
    "size": "large",
    "colors": ["red", "blue"],
    "attributes": [{"pockets": 2}, {"washable": "no"}],
    "data": {
      "name": "example",
      "isNew": true,
      "isDeleted": false,
      "location": {
        "x": 123,
        "y": 456
      }
    }
  }
Example Output:
  Array of arrays with key/value pairs:
  [
    ["size", "large"],
    ["colors[]", "red"],
    ["colors[]", "blue"],
    ["attributes[][pockets]", 2],
    ["attributes[][washable]", "no"],
    ["data[name]", "example"],
    ["data[isNew]", true],
    ["data[isDeleted]", false],
    ["data[location][x]", 123],
    ["data[location][y]", 456]
  ]
 */

function pathString(parts, shouldDecamelize) {
  // Path is first key, followed by remaining keys surrounded by square braces
  return parts[0] + parts.slice(1).map(key => shouldDecamelize ? `[${decamelize(key)}]` : `[${key}]`).join('');
}
function areNullable(parts, options) {
  const flattenedParts = parts.join('.');
  for (const option of options) {
    if (flattenedParts === option) {
      return true;
    }
  }
  return false;
}
export default function multipartFormSerialize(input, parts = [], results = [], needsDecamelized = false, nullableParts = []) {
  // Important to note that we are allowing empty strings to pass through.
  const notNullOrFalseOrEmpty = input || input === false || input === '';
  const nullAndNullable = input === null && areNullable(parts, nullableParts);
  if (notNullOrFalseOrEmpty || nullAndNullable) {
    if (input?.constructor === Object) {
      for (const key in input) {
        const value = input[key];
        needsDecamelized ? multipartFormSerialize(value, [...parts, decamelize(key)], results, needsDecamelized, nullableParts) : multipartFormSerialize(value, [...parts, key], results, needsDecamelized, nullableParts);
      }
    } else if (input?.constructor === Array) {
      for (const value of input) {
        // Array keys are left blank and auto-numbered by the server
        multipartFormSerialize(value, [...parts, ''], results, needsDecamelized, nullableParts);
      }
    } else {
      results.push([pathString(parts, needsDecamelized), input]);
    }
  }
  return results;
}